import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as adminSubscriptionServices from "../../../contexts/services/admin/adminSubscriptionServices";
import SecurityGuard from "../../guards/SecurityGuard";
import { useGuardRequest } from "../../context/useGuardRequest";
import ProcessingDialog from "../../library/ProcessingDialog";
import Dialog from "../../library/Dialog";

const paperBorderSx = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  overflow: "hidden",
};

const headCellSx = {
  fontWeight: 600,
  color: "text.secondary",
  borderColor: "divider",
  py: 1.5,
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
};

const bodyCellSx = {
  py: 1.5,
  borderColor: "divider",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const toDateTimeInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

function isValidIpAddress(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  const ipv4Regex =
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
  if (ipv4Regex.test(trimmed)) return true;

  const ipv6Regex =
    /^(?:[0-9A-Fa-f]{1,4}:){1,7}[0-9A-Fa-f]{1,4}$|^(?:[0-9A-Fa-f]{1,4}:){1,7}:$|^:(?::[0-9A-Fa-f]{1,4}){1,7}$|^::$|^(?:[0-9A-Fa-f]{1,4}:){1,6}:[0-9A-Fa-f]{1,4}$/;
  return ipv6Regex.test(trimmed);
}

function getBlockedIpValidationErrors(values) {
  const errors = {};
  const ip = values?.ip_address?.trim() || "";
  if (!ip) {
    errors.ip_address = "IP address is required.";
  } else if (!isValidIpAddress(ip)) {
    errors.ip_address = "Enter a valid IPv4 or IPv6 address.";
  }

  if (values?.expires_at) {
    const parsed = new Date(values.expires_at);
    if (Number.isNaN(parsed.getTime())) {
      errors.expires_at = "Enter a valid date and time.";
    }
  }

  return errors;
}

const normalizeResponseData = (response) =>
  response?.data?.data || response?.data || {};

const SecurityConsole = () => {
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockedIpsLoading, setBlockedIpsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [ipTouched, setIpTouched] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [blockedIpForm, setBlockedIpForm] = useState({
    ip_address: "",
    reason: "",
    expires_at: "",
    is_active: true,
  });
  const [editingBlockedIpId, setEditingBlockedIpId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [snack, setSnack] = useState(null);

  const { guardProps, runActionWithMiddleware } = useGuardRequest({
    policy: {
      manageNetworkRestrictions: true,
    },
  });

  const blockedIpErrors = useMemo(
    () => getBlockedIpValidationErrors(blockedIpForm),
    [blockedIpForm]
  );
  const busy = updating || guardProps.open;
  const isEditing = editingBlockedIpId !== null;

  const loadBlockedIps = async () => {
    setBlockedIpsLoading(true);
    setLoadError("");
    try {
      const res = await adminSubscriptionServices.getBlockedIps();
      const payload = normalizeResponseData(res);
      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.blocked_ips)
          ? payload.blocked_ips
          : [];
      setBlockedIps(items);
    } catch (error) {
      setLoadError(
        "Could not refresh restrictions. The list may be out of date. Try again."
      );
    } finally {
      setBlockedIpsLoading(false);
    }
  };

  useEffect(() => {
    void loadBlockedIps();
  }, []);

  const resetBlockedIpForm = () => {
    setIpTouched(false);
    setSaveError("");
    setEditingBlockedIpId(null);
    setBlockedIpForm({
      ip_address: "",
      reason: "",
      expires_at: "",
      is_active: true,
    });
  };

  const handleBlockedIpSubmit = async () => {
    if (busy) return;
    setIpTouched(true);
    setSaveError("");
    const ip = blockedIpForm.ip_address.trim();
    const validationErrors = getBlockedIpValidationErrors(blockedIpForm);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const payload = {
      ip_address: ip,
      reason: blockedIpForm.reason.trim() || "No reason provided",
      expires_at: blockedIpForm.expires_at
        ? new Date(blockedIpForm.expires_at).toISOString()
        : null,
      is_active: blockedIpForm.is_active,
    };

    await runActionWithMiddleware({
      actionKey: "manageNetworkRestrictions",
      details: (
        <Typography variant='body2' color='text.secondary'>
          {isEditing ? "Update" : "Create"} network restriction for {ip}.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          if (isEditing) {
            await adminSubscriptionServices.updateBlockedIp(
              editingBlockedIpId,
              {
                reason: payload.reason,
                expires_at: payload.expires_at,
                is_active: payload.is_active,
              }
            );
            setSnack({ message: "Blocked IP updated" });
          } else {
            await adminSubscriptionServices.createBlockedIp(payload);
            setSnack({ message: "Blocked IP added" });
          }
          resetBlockedIpForm();
          setFormOpen(false);
          await loadBlockedIps();
        } catch (error) {
          setSaveError(
            error?.response?.data?.message ||
              "Could not save this restriction. Try again."
          );
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const handleBlockedIpEdit = (entry) => {
    if (busy) return;
    setSaveError("");
    setIpTouched(false);
    setFormOpen(true);
    setEditingBlockedIpId(entry.id);
    setBlockedIpForm({
      ip_address: entry.ip_address || "",
      reason: entry.reason || "",
      expires_at: toDateTimeInputValue(entry.expires_at),
      is_active: entry.is_active !== false,
    });
  };

  const handleBlockedIpDelete = async (entry) => {
    if (busy) return;
    await runActionWithMiddleware({
      actionKey: "manageNetworkRestrictions",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Delete the restriction for {entry.ip_address}. This removes this rule
          from the list.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          await adminSubscriptionServices.deleteBlockedIp(entry.id);
          setSnack({ message: "Blocked IP removed" });
          await loadBlockedIps();
        } catch (error) {
          setSnack({
            message:
              error?.response?.data?.message || "Failed to remove blocked IP",
            severity: "error",
          });
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, lg: 3 },
        minWidth: 0,
        "& .MuiButton-root": {
          textTransform: "none",
          borderRadius: 1.5,
          fontWeight: 600,
        },
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent='space-between'
          gap={2}
        >
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Avatar
              variant='rounded'
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                color: "primary.main",
              }}
            >
              <SecurityIcon />
            </Avatar>
            <Box>
              <Typography component='h1' variant='h5' fontWeight={750}>
                Security
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Manage IP addresses restricted from accessing the platform.
              </Typography>
            </Box>
          </Stack>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Button
              variant='outlined'
              startIcon={
                blockedIpsLoading ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <RefreshRoundedIcon />
                )
              }
              disabled={busy || blockedIpsLoading}
              onClick={loadBlockedIps}
            >
              Refresh
            </Button>
            <Button
              variant='contained'
              startIcon={<AddRoundedIcon />}
              disabled={busy}
              onClick={() => {
                resetBlockedIpForm();
                setFormOpen(true);
              }}
              sx={{ boxShadow: "none", whiteSpace: "nowrap" }}
            >
              Add restriction
            </Button>
          </Stack>
        </Stack>
        <Paper
          elevation={0}
          sx={{
            ...paperBorderSx,
            p: 2,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.025),
          }}
        >
          <Stack direction='row' spacing={1.5} alignItems='flex-start'>
            <InfoOutlinedIcon
              sx={{ color: "primary.main", fontSize: 20, mt: 0.25 }}
            />
            <Box>
              <Typography variant='body2' fontWeight={700}>
                Access rules for the platform
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5 }}
              >
                Restrictions apply to everyone using the specified IP address.
                Adding, changing or deleting a rule requires security
                verification.
              </Typography>
            </Box>
          </Stack>
        </Paper>
        <Paper
          component='section'
          aria-labelledby='restrictions-title'
          elevation={0}
          sx={{
            ...paperBorderSx,
            boxShadow: "0 3px 16px rgba(30,20,50,0.025)",
          }}
        >
          <Box sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}>
            <Stack direction='row' alignItems='center' gap={1}>
              <Typography
                id='restrictions-title'
                component='h2'
                variant='subtitle1'
                fontWeight={700}
              >
                IP restrictions
              </Typography>
              {!blockedIpsLoading && !loadError && (
                <Chip
                  size='small'
                  label={`${blockedIps.length} ${blockedIps.length === 1 ? "rule" : "rules"}`}
                  sx={{ height: 24, fontWeight: 600 }}
                />
              )}
            </Stack>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Review each rule's status, reason and expiry. Times are shown in
              your local time.
            </Typography>
            {blockedIps.length > 0 && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: { xs: "block", md: "none" }, mt: 1 }}
              >
                Scroll the table sideways to see expiry dates and actions.
              </Typography>
            )}
          </Box>
          {loadError && (
            <Alert
              severity='error'
              variant='outlined'
              sx={{ m: 2 }}
              action={
                <Button
                  color='inherit'
                  size='small'
                  onClick={loadBlockedIps}
                  disabled={busy || blockedIpsLoading}
                >
                  Retry
                </Button>
              }
            >
              {loadError}
            </Alert>
          )}
          {blockedIpsLoading ? (
            <Stack
              role='status'
              direction='row'
              spacing={1.5}
              justifyContent='center'
              alignItems='center'
              sx={{ p: 5 }}
            >
              <CircularProgress size={20} />
              <Typography variant='body2' color='text.secondary'>
                Loading restrictions...
              </Typography>
            </Stack>
          ) : blockedIps.length === 0 ? (
            !loadError && (
              <Stack
                alignItems='center'
                spacing={1}
                sx={{ px: 2, py: 5, textAlign: "center" }}
              >
                <SecurityIcon sx={{ color: "text.disabled", fontSize: 32 }} />
                <Typography variant='subtitle2' fontWeight={700}>
                  No IP restrictions
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Add a restriction when an IP address needs to be blocked.
                </Typography>
              </Stack>
            )
          ) : (
            <TableContainer
              tabIndex={0}
              role='region'
              aria-label='IP restrictions table'
              sx={{ maxHeight: "65vh" }}
            >
              <Table
                size='small'
                stickyHeader
                aria-label='IP restrictions'
                sx={{
                  minWidth: 760,
                  "& tr:last-child td": { borderBottom: 0 },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      "IP address / reason",
                      "Status",
                      "Expires",
                      "Actions",
                    ].map((label) => (
                      <TableCell
                        key={label}
                        sx={{ ...headCellSx, bgcolor: "background.default" }}
                        align={label === "Actions" ? "right" : "left"}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blockedIps.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 400 }}>
                        <Typography
                          variant='body2'
                          fontWeight={650}
                          sx={{
                            fontFamily: "monospace",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {entry.ip_address || "Unknown address"}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mt: 0.5, overflowWrap: "anywhere" }}
                        >
                          {entry.reason || "No reason provided"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip
                          size='small'
                          variant='outlined'
                          label={
                            entry.is_active === false ? "Disabled" : "Enabled"
                          }
                          color={
                            entry.is_active === false ? "default" : "success"
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: "nowrap" }}>
                        {entry.expires_at
                          ? formatDateTime(entry.expires_at)
                          : "No expiry"}
                      </TableCell>
                      <TableCell sx={bodyCellSx} align='right'>
                        <Stack
                          direction='row'
                          justifyContent='flex-end'
                          spacing={1}
                        >
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<EditOutlinedIcon />}
                            aria-label={
                              "Edit restriction for " + entry.ip_address
                            }
                            disabled={busy}
                            onClick={() => handleBlockedIpEdit(entry)}
                          >
                            Edit
                          </Button>
                          <Button
                            size='small'
                            color='error'
                            startIcon={<DeleteOutlineRoundedIcon />}
                            aria-label={
                              "Delete restriction for " + entry.ip_address
                            }
                            disabled={busy}
                            onClick={() => handleBlockedIpDelete(entry)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
      <MuiDialog
        open={formOpen}
        onClose={busy ? undefined : () => setFormOpen(false)}
        fullWidth
        maxWidth='sm'
        aria-labelledby='restriction-form-title'
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: 1,
            borderColor: "divider",
            m: { xs: 2, sm: 4 },
            width: "calc(100% - 32px)",
            maxHeight: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle
          id='restriction-form-header'
          component='div'
          sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Stack direction='row' spacing={1.5} alignItems='flex-start'>
            <Avatar
              variant='rounded'
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "action.hover",
                color: "primary.main",
              }}
            >
              <SecurityIcon fontSize='small' />
            </Avatar>
            <Box>
              <Typography
                id='restriction-form-title'
                component='h2'
                variant='subtitle1'
                fontWeight={700}
              >
                {isEditing ? "Edit restriction" : "Add restriction"}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5 }}
              >
                Choose the address, reason and how long the rule should apply.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, "&.MuiDialogContent-root": { pt: 2.5 } }}>
          <Stack
            component='form'
            id='restriction-form'
            spacing={2.5}
            onSubmit={(event) => {
              event.preventDefault();
              handleBlockedIpSubmit();
            }}
          >
            {saveError && (
              <Alert severity='error' variant='outlined'>
                {saveError}
              </Alert>
            )}
            <TextField
              autoFocus={!isEditing}
              fullWidth
              size='small'
              label='IP address'
              disabled={busy || isEditing}
              value={blockedIpForm.ip_address}
              onBlur={() => setIpTouched(true)}
              onChange={(e) =>
                setBlockedIpForm((prev) => ({
                  ...prev,
                  ip_address: e.target.value,
                }))
              }
              placeholder='192.168.1.10'
              error={ipTouched && Boolean(blockedIpErrors.ip_address)}
              helperText={
                isEditing
                  ? "To use another IP address, add a new restriction."
                  : (ipTouched && blockedIpErrors.ip_address) ||
                    "Enter an IPv4 or IPv6 address."
              }
            />
            <TextField
              autoFocus={isEditing}
              fullWidth
              size='small'
              label='Reason (optional)'
              disabled={busy}
              value={blockedIpForm.reason}
              onChange={(e) =>
                setBlockedIpForm((prev) => ({
                  ...prev,
                  reason: e.target.value,
                }))
              }
              placeholder='For example, repeated suspicious activity'
              multiline
              minRows={2}
            />
            <TextField
              fullWidth
              size='small'
              label='Expiry date and time (optional)'
              type='datetime-local'
              disabled={busy}
              InputLabelProps={{ shrink: true }}
              value={blockedIpForm.expires_at}
              onChange={(e) =>
                setBlockedIpForm((prev) => ({
                  ...prev,
                  expires_at: e.target.value,
                }))
              }
              error={Boolean(blockedIpErrors.expires_at)}
              helperText={
                blockedIpErrors.expires_at ||
                "Your local time. Leave blank for no expiry."
              }
            />
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                px: 2,
                py: 1.5,
              }}
            >
              <FormControlLabel
                sx={{ m: 0, width: "100%", justifyContent: "space-between" }}
                labelPlacement='start'
                control={
                  <Switch
                    disabled={busy}
                    checked={blockedIpForm.is_active}
                    onChange={(e) =>
                      setBlockedIpForm((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  <Typography variant='body2' fontWeight={650}>
                    Enable restriction
                  </Typography>
                }
              />
              <Typography variant='body2' color='text.secondary'>
                {blockedIpForm.is_active
                  ? "Block this address until the rule expires or is disabled."
                  : "Keep this rule saved without blocking the address."}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: 1,
            borderColor: "divider",
            "& .MuiButton-root": {
              textTransform: "none",
              borderRadius: 1.5,
              fontWeight: 600,
            },
          }}
        >
          <Button
            color='inherit'
            disabled={busy}
            onClick={() => setFormOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            type='submit'
            form='restriction-form'
            disabled={busy || Object.keys(blockedIpErrors).length > 0}
            sx={{ boxShadow: "none" }}
          >
            {isEditing ? "Save changes" : "Add restriction"}
          </Button>
        </DialogActions>
      </MuiDialog>
      <ProcessingDialog loading={updating} />
      <SecurityGuard {...guardProps} />
      <Dialog
        open={Boolean(snack)}
        onClose={() => setSnack(null)}
        title={
          snack?.severity === "error"
            ? "Could not complete this action"
            : "Restriction updated"
        }
        content={snack?.message || ""}
        confirmText='OK'
        cancelText='Close'
        onConfirm={() => setSnack(null)}
        maxWidth='xs'
        fullScreen={false}
        paperSx={{ borderRadius: 3, border: 1, borderColor: "divider" }}
        headerSx={{ p: 2.5 }}
        contentSx={{ p: 2.5, overflowWrap: "anywhere" }}
        actionsSx={{ px: 2.5, py: 1.5 }}
        confirmButtonSx={{
          boxShadow: "none",
          borderRadius: 1.5,
          textTransform: "none",
        }}
      />
    </Box>
  );
};
export default SecurityConsole;
